import React, { useEffect, useState } from "react";
import "../../styles/clients.css";
import "../../styles/users.css";
import { Plus, Edit, Trash2, RefreshCw, X, XCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search } from "lucide-react";
import { useGeneric } from "../../hooks/useGeneric";
import { confirmAndDelete } from "../../utils/deleteHelper";
import { vbCrypt, vbDecrypt } from "../../utils/functions";
import { matchesAgency } from "../../utils/agencySearch";
import { supabase } from "../../api/supabaseClient";

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const EMPTY_FORM = {
    user_id: null,
    user_name: "",
    mobile_number: "",
    password: "",
    device_id: "",
    agency_id: [],
};

const UserScreen = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [showForm, setShowForm] = useState(false);
    const [errors, setErrors] = useState({});
    const [search, setSearch] = useState("");
    const [saving, setSaving] = useState(false);
    const [decrypting, setDecrypting] = useState(false);
    const [agencySearch, setAgencySearch] = useState("");

    // ── Pagination ───────────────────────────────────────────────────
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);

    const { data: users = [], loading, fetchAll: fetchUsers, save: saveUser, remove: deleteUser } = useGeneric({
        tableName: "users",
        idColumn: "user_id",
        orderBy: "user_name",
        removeOnInsert: ["user_id"],
    });

    const { data: agencies = [], fetchAll: fetchAgencies } = useGeneric({
        tableName: "agencies",
        idColumn: "agency_id",
        orderBy: "agency_name",
    });

    useEffect(() => { fetchUsers(); fetchAgencies(); }, [fetchUsers, fetchAgencies]);
    useEffect(() => { setCurrentPage(1); }, [search, pageSize]);

    const set = (key, val) => {
        setForm((prev) => ({ ...prev, [key]: val }));
        setErrors((prev) => ({ ...prev, [key]: "" }));
    };

    // ── Multi-select agency toggle ───────────────────────────────────
    const toggleAgency = (agencyId) => {
        const id = Number(agencyId);
        setForm((prev) => {
            const current = (prev.agency_id ?? []).map(Number);
            const already = current.includes(id);
            return {
                ...prev,
                agency_id: already ? current.filter((a) => a !== id) : [...current, id],
            };
        });
        setErrors((prev) => ({ ...prev, agency_id: "" }));
    };

    // ── Validation ───────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        if (!form.user_name.trim()) errs.user_name = "User name is required.";
        else if (form.user_name.length > 50) errs.user_name = "Max 50 characters.";
        if (!form.mobile_number.trim()) errs.mobile_number = "Mobile number is required.";
        else if (!/^\d{10}$/.test(form.mobile_number.trim())) errs.mobile_number = "Mobile must be 10 digits.";
        if (!form.password.trim()) errs.password = "Password is required.";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Save ─────────────────────────────────────────────────────────
    // ── Duplicate mobile check (against the database, not just the list) ──
    const mobileExists = async (mobile, excludeUserId) => {
        let query = supabase
            .from("users")
            .select("user_id, user_name")
            .eq("mobile_number", mobile)
            .limit(1);
        if (excludeUserId) query = query.neq("user_id", excludeUserId);
        const { data, error } = await query;
        if (error) throw new Error("Mobile check failed: " + error.message);
        return data && data.length > 0 ? data[0] : null;
    };

    const onSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const mobile = form.mobile_number.trim();
            const existing = await mobileExists(mobile, form.user_id);
            if (existing) {
                setErrors((prev) => ({
                    ...prev,
                    mobile_number: `Mobile number already exists (User: ${existing.user_name}).`,
                }));
                return;
            }

            const encryptedPassword = await vbCrypt(form.password);
            const payload = { ...form, mobile_number: mobile, password: encryptedPassword };
            if (!payload.user_id) delete payload.user_id;
            await saveUser(payload);
            closeModal();
        } catch (err) {
            console.error("Save failed", err);
            // 23505 = unique_violation (in case another save slipped in at the same time)
            if (err?.code === "23505") {
                setErrors((prev) => ({ ...prev, mobile_number: "Mobile number already exists." }));
            } else {
                alert("Save failed: " + (err.message || "Unknown error"));
            }
        } finally {
            setSaving(false);
        }
    };

    // ── Modal helpers ────────────────────────────────────────────────
    const openAdd = () => { setForm(EMPTY_FORM); setErrors({}); setAgencySearch(""); setShowForm(true); };

    const openEdit = async (user) => {
        setDecrypting(true);
        setShowForm(true);
        setAgencySearch("");

        // Parse agency_id array
        let agencyIds = user.agency_id ?? [];
        if (typeof agencyIds === "string") {
            try { agencyIds = JSON.parse(agencyIds); } catch { agencyIds = []; }
        }
        if (!Array.isArray(agencyIds)) agencyIds = [];

        // Decrypt the stored password before showing it
        let plainPassword = "";
        try {
            if (user.password) {
                plainPassword = await vbDecrypt(user.password);
            }
        } catch {
            plainPassword = "";
        }

        setForm({
            ...user,
            password: plainPassword,
            agency_id: agencyIds.map(Number),
        });
        setErrors({});
        setDecrypting(false);
    };

    const closeModal = () => { setShowForm(false); setForm(EMPTY_FORM); setErrors({}); setDecrypting(false); setAgencySearch(""); };

    const handleDelete = (user, e) => {
        e.stopPropagation();
        confirmAndDelete({
            what: "user",
            name: user.user_name,
            run: () => deleteUser(user.user_id),
        });
    };

    // ── Filter & sort ────────────────────────────────────────────────
    const filtered = (users || [])
        .filter((u) =>
            u.user_name?.toLowerCase().includes(search.toLowerCase()) ||
            u.mobile_number?.includes(search)
        )
        .sort((a, b) => a.user_name?.localeCompare(b.user_name));

    // ── Paginate ─────────────────────────────────────────────────────
    const totalRecords = filtered.length;
    const totalPages = Math.max(1, Math.ceil(totalRecords / pageSize));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * pageSize;
    const paginated = filtered.slice(startIdx, startIdx + pageSize);

    const goTo = (page) => setCurrentPage(Math.max(1, Math.min(page, totalPages)));

    const getPageNumbers = () => {
        const pages = [];
        const left = Math.max(1, safePage - 2);
        const right = Math.min(totalPages, safePage + 2);
        for (let i = left; i <= right; i++) pages.push(i);
        return pages;
    };

    return (
        <div className="cl-page">
            <div className="cl-panel">
                {/* ── Heading ── */}
                <div className="cl-panel-heading">
                    <span>User Details</span>
                    <button className="cl-add-btn" onClick={openAdd} title="Add User">
                        <Plus size={16} />
                    </button>
                </div>

                {/* ── Search ── */}
                <div className="cl-search-bar">
                    <input type="text" placeholder="Search users…" value={search}
                        onChange={(e) => setSearch(e.target.value)} className="cl-search-input" />
                    <button className="cl-refresh-btn" onClick={fetchUsers} title="Refresh">
                        <RefreshCw size={15} />
                    </button>
                </div>

                {/* ── Table ── */}
                {loading ? (
                    <div className="cl-loading">Loading…</div>
                ) : (
                    <>
                        <div className="cl-table-wrapper">
                            <table className="cl-table">
                                <thead>
                                    <tr>
                                        <th>#</th>
                                        <th>User ID</th>
                                        <th>User Name</th>
                                        <th>Mobile Number</th>
                                        <th>Device ID</th>
                                        <th className="cl-actions-th">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {paginated.length === 0 ? (
                                        <tr><td colSpan={6} className="cl-empty">No users found.</td></tr>
                                    ) : (
                                        paginated.map((u, i) => (
                                            <tr
                                                key={u.user_id ?? i}
                                                className={`${i % 2 === 0 ? "cl-row-even" : "cl-row-odd"} cl-row-clickable`}
                                                onClick={() => openEdit(u)}
                                                title="Click to edit"
                                            >
                                                <td data-label="#">{startIdx + i + 1}</td>
                                                <td data-label="User ID">{u.user_id}</td>
                                                <td data-label="User Name">{u.user_name}</td>
                                                <td data-label="Mobile">{u.mobile_number}</td>
                                                <td data-label="Device ID">{u.device_id}</td>
                                                <td className="cl-actions-td" onClick={(e) => e.stopPropagation()}>
                                                    <button className="cl-icon-btn cl-edit" onClick={() => openEdit(u)} title="Edit">
                                                        <Edit size={15} /><span className="cl-icon-label">Edit</span>
                                                    </button>
                                                    <button className="cl-icon-btn cl-delete" onClick={(e) => handleDelete(u, e)} title="Delete">
                                                        <Trash2 size={15} /><span className="cl-icon-label">Delete</span>
                                                    </button>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>

                        {/* ── Pagination ── */}
                        <div className="cl-pagination">
                            <div className="cl-pagination-info">
                                {totalRecords === 0 ? "No records" : `Showing ${startIdx + 1}–${Math.min(startIdx + pageSize, totalRecords)} of ${totalRecords}`}
                            </div>
                            <div className="cl-pagination-controls">
                                <button className="cl-page-btn" onClick={() => goTo(1)} disabled={safePage === 1} title="First"><ChevronsLeft size={15} /></button>
                                <button className="cl-page-btn" onClick={() => goTo(safePage - 1)} disabled={safePage === 1} title="Previous"><ChevronLeft size={15} /></button>
                                {getPageNumbers()[0] > 1 && (
                                    <><button className="cl-page-btn" onClick={() => goTo(1)}>1</button>
                                        {getPageNumbers()[0] > 2 && <span className="cl-page-ellipsis">…</span>}</>
                                )}
                                {getPageNumbers().map((p) => (
                                    <button key={p} className={`cl-page-btn ${p === safePage ? "cl-page-active" : ""}`} onClick={() => goTo(p)}>{p}</button>
                                ))}
                                {getPageNumbers()[getPageNumbers().length - 1] < totalPages && (
                                    <>{getPageNumbers()[getPageNumbers().length - 1] < totalPages - 1 && <span className="cl-page-ellipsis">…</span>}
                                        <button className="cl-page-btn" onClick={() => goTo(totalPages)}>{totalPages}</button></>
                                )}
                                <button className="cl-page-btn" onClick={() => goTo(safePage + 1)} disabled={safePage === totalPages} title="Next"><ChevronRight size={15} /></button>
                                <button className="cl-page-btn" onClick={() => goTo(totalPages)} disabled={safePage === totalPages} title="Last"><ChevronsRight size={15} /></button>
                            </div>
                            <div className="cl-pagination-size">
                                <label>Rows:</label>
                                <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}>
                                    {PAGE_SIZE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                                </select>
                            </div>
                        </div>
                    </>
                )}

                <div className="cl-panel-footer" />
            </div>

            {/* ── Modal ── */}
            {showForm && (
                <div className="cl-modal-backdrop">
                    <div className="cl-modal-overlay" />
                    <div className="cl-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="cl-modal-header">
                            <h4 className="cl-modal-title">{form.user_id ? "Edit User" : "Add User"}</h4>
                            <button className="cl-modal-close" onClick={closeModal}><X size={18} /></button>
                        </div>

                        <div className="cl-modal-body">
                            {decrypting ? (
                                <div className="cl-loading" style={{ padding: "40px 0", textAlign: "center" }}>
                                    Decrypting password…
                                </div>
                            ) : (
                                <form autoComplete="off">
                                    {/* User Name | User ID */}
                                    <div className="cl-form-group cl-row">
                                        <div className="cl-col-10">
                                            <label className="cl-control-label">User Name</label>
                                            <input type="text"
                                                className={`cl-form-control${errors.user_name ? " cl-input-error" : ""}`}
                                                placeholder="User Name" maxLength={50}
                                                value={form.user_name}
                                                onChange={(e) => set("user_name", e.target.value)} />
                                            {errors.user_name && <span className="cl-error-msg">{errors.user_name}</span>}
                                        </div>
                                        <div className="cl-col-2">
                                            <label className="cl-control-label">User ID</label>
                                            <input type="text" className="cl-form-control cl-readonly"
                                                placeholder="Auto" value={form.user_id ?? ""} readOnly />
                                        </div>
                                    </div>

                                    {/* Mobile | Password */}
                                    <div className="cl-form-group cl-row">
                                        <div className="cl-col-6">
                                            <label className="cl-control-label">Mobile Number</label>
                                            <input type="tel"
                                                className={`cl-form-control${errors.mobile_number ? " cl-input-error" : ""}`}
                                                placeholder="Mobile Number" maxLength={10}
                                                value={form.mobile_number}
                                                onChange={(e) => set("mobile_number", e.target.value.replace(/\D/g, ""))} />
                                            {errors.mobile_number && <span className="cl-error-msg">{errors.mobile_number}</span>}
                                        </div>
                                        <div className="cl-col-6">
                                            <label className="cl-control-label">
                                                Password
                                                {form.user_id && (
                                                    <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6, fontSize: 12 }}>
                                                        (decrypted)
                                                    </span>
                                                )}
                                            </label>
                                            <input type="text"
                                                className={`cl-form-control${errors.password ? " cl-input-error" : ""}`}
                                                placeholder="Password" maxLength={50}
                                                value={form.password}
                                                onChange={(e) => set("password", e.target.value)} />
                                            {errors.password && <span className="cl-error-msg">{errors.password}</span>}
                                        </div>
                                    </div>

                                    {/* Device ID */}
                                    <div className="cl-form-group cl-row">
                                        <div className="cl-col-12">
                                            <label className="cl-control-label">Device ID</label>
                                            <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
                                                <input type="text" className="cl-form-control cl-readonly"
                                                    placeholder="Device ID (auto-assigned)"
                                                    value={form.device_id ?? ""} readOnly />
                                                <button type="button" className="cl-refresh-icon-btn"
                                                    onClick={() => set("device_id", "")} title="Clear Device ID"
                                                    style={{ color: "#dc3545", borderColor: "#dc3545" }}>
                                                    <XCircle size={16} />
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Agency Multi-Select */}
                                    <div className="cl-form-group cl-row">
                                        <div className="cl-col-12">
                                            <label className="cl-control-label">
                                                Agencies
                                                <span style={{ fontWeight: 400, color: "#6b7280", marginLeft: 6, fontSize: 12 }}>
                                                    (select one or more)
                                                </span>
                                            </label>
                                            <div className="cl-agency-multiselect-row"
                                                style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                                                <div className="cl-agency-multiselect-col">
                                                    <div className="cl-agency-search-wrapper">
                                                        <Search size={14} className="cl-agency-search-icon" />
                                                        <input
                                                            type="text"
                                                            className="cl-agency-search"
                                                            placeholder="Search agencies…"
                                                            value={agencySearch}
                                                            onChange={(e) => setAgencySearch(e.target.value)}
                                                        />
                                                        {agencySearch && (
                                                            <button type="button" className="cl-agency-search-clear"
                                                                onClick={() => setAgencySearch("")} title="Clear search">
                                                                <X size={13} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="cl-agency-multiselect">
                                                        {agencies.length === 0 ? (
                                                            <div className="cl-agency-empty">No agencies available</div>
                                                        ) : (
                                                            (() => {
                                                                const visibleAgencies = agencies
                                                                    .slice()
                                                                    .sort((a, b) => a.agency_name?.localeCompare(b.agency_name))
                                                                    .filter((agency) => matchesAgency(agency, agencySearch));
                                                                if (visibleAgencies.length === 0) {
                                                                    return <div className="cl-agency-empty">No agencies match your search</div>;
                                                                }
                                                                return visibleAgencies.map((agency) => {
                                                                    const isSelected = (form.agency_id ?? [])
                                                                        .map(Number).includes(Number(agency.agency_id));
                                                                    return (
                                                                        <div key={agency.agency_id}
                                                                            className={`cl-agency-option${isSelected ? " cl-agency-selected" : ""}`}
                                                                            onClick={() => toggleAgency(agency.agency_id)}>
                                                                            <span className="cl-agency-check">{isSelected ? "✓" : ""}</span>
                                                                            <span>{agency.agency_name}</span>
                                                                            <span className="cl-agency-sub">
                                                                                {[agency.software_key, agency.mobile || agency.phone].filter(Boolean).join(" · ")}
                                                                            </span>
                                                                        </div>
                                                                    );
                                                                });
                                                            })()
                                                        )}
                                                    </div>
                                                </div>
                                                <button type="button" className="cl-refresh-icon-btn"
                                                    onClick={fetchAgencies} title="Refresh Agencies">
                                                    <RefreshCw size={14} />
                                                </button>
                                            </div>
                                            {errors.agency_id && <span className="cl-error-msg">{errors.agency_id}</span>}
                                            {(form.agency_id ?? []).length > 0 && (
                                                <div className="cl-agency-tags">
                                                    {form.agency_id.map((id) => {
                                                        const agency = agencies.find((ag) => Number(ag.agency_id) === Number(id));
                                                        return (
                                                            <span key={id} className="cl-agency-tag">
                                                                {agency?.agency_name ?? id}
                                                                <button type="button" className="cl-agency-tag-remove"
                                                                    onClick={() => toggleAgency(id)}>×</button>
                                                            </span>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </form>
                            )}
                        </div>

                        <div className="cl-modal-footer">
                            <button className="cl-btn cl-btn-default" onClick={closeModal}>Close</button>
                            <button className="cl-btn cl-btn-primary" onClick={onSave} disabled={saving || decrypting}>
                                {saving ? "Saving…" : form.user_id ? "Update" : "Add"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UserScreen;